import styled, { useTheme } from 'styled-components';
import uniqueId from 'lodash/uniqueId';
import createLinkRel from '~/helpers/createLinkRel';
import Logo from '../../atoms/Logo';
import FooterSmall from '../FooterSmall';
import UrlContext from '~/helpers/UrlContext';
import Col from '~/grid/Col';
import Row from '~/grid/Row';
import Container from '~/grid/Container';
import CTA from '../../atoms/CTA';
import FooterCtaColumn from './partials/FooterCtaColumn';
import SocialMediaButtons from './partials/SocialMediaButtons';
import FooterBigLinksWrapperColStyled, { RowStyled } from './FooterBig.styles';
import { useContext } from 'react';

/**
 * @param {FooterType} props
 * @returns {string}
 */
const getMobileSwitch = (props) => props.theme.footer.mobileSwitch;

const FooterStyled = styled.div`
  position: relative;
  width: 100%;
  background: ${(props) => props.theme.color.gradientprimary700};
  display: inline-block;
  padding: 70px 0px;
  @media (${(props) => getMobileSwitch(props)}) {
    padding: 40px 0px;
  }
`;

const FooterContainer = styled(Container)`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  flex-direction: row;
  @media (${(props) => getMobileSwitch(props)}) {
    align-items: flex-start;
    flex-direction: column;
  }
`;

const LogoWrapper = styled.a`
  display: block;
  margin-bottom: 35px;
`;

const StyledLogo = styled(Logo)`
  color: ${(props) => props.theme.color.white500};
  & svg * {
    fill: ${(props) => props.theme.color.white500};
  }
`;

const LinksWrapper = styled(Row)`
  justify-content: flex-start;
  @media (${(props) => getMobileSwitch(props)}) {
    gap: 24px;
  }
`;

const SocialMediaWrapper = styled.div`
  margin-top: 50px;
`;

/**
 * @param {FooterBigProps} props
 * @returns {React.ReactElement} The rendered component
 */
const FooterBig = ({
  logoLink,
  footerLinks,
  socialMediaButtons,
  footerSmallProps,
  footerCta,
  minimalFooter = false,
  className,
}) => {
  const createUrl = useContext(UrlContext);
  const theme = useTheme();

  const disableBigFooter = !theme.footer.showBigFooter || minimalFooter;

  /**
   * @type {Breakpoints}
   */
  const linksColWidth = () => {
    if (footerCta) {
      return {
        xxl: 9,
        xl: 8,
        lg: 8,
        md: 12,
        sm: 12,
        xs: 12,
      };
    }
    return {
      xxl: 12,
      xl: 12,
      lg: 12,
      md: 12,
      sm: 12,
      xs: 12,
    };
  };

  return (
    <>
      {!disableBigFooter && (
        <FooterStyled className={className} data-id="footer-big">
          <FooterContainer>
            <LogoWrapper
              {...logoLink}
              href={createUrl(logoLink.href)}
              rel={createLinkRel(logoLink.target)}
            >
              <StyledLogo isFooter />
            </LogoWrapper>
          </FooterContainer>
          <FooterContainer data-id="footer-big-container">
            <RowStyled dataId="footer-big-row">
              <Col colAmount={linksColWidth()} dataId="footer-big-col">
                <LinksWrapper dataId="footer-big-links-wrapper-row">
                  {footerLinks.map((linkGroup) => (
                    <FooterBigLinksWrapperColStyled
                      key={uniqueId('footerNavLinkGroup_')}
                      colAmount={{ xl: 4, lg: 4, md: 12, sm: 12, xs: 12 }}
                      dataId="footer-big-links-wrapper-col"
                    >
                      {linkGroup.map((link) => (
                        <CTA
                          skin="NAVIGATION_FOOTER"
                          isBrand
                          {...link}
                          key={link.href}
                        />
                      ))}
                    </FooterBigLinksWrapperColStyled>
                  ))}
                </LinksWrapper>
              </Col>
              {footerCta && <FooterCtaColumn {...footerCta} />}
            </RowStyled>
          </FooterContainer>
          <SocialMediaWrapper data-id="footer-big-social-media-wrapper">
            <SocialMediaButtons {...socialMediaButtons} />
          </SocialMediaWrapper>
        </FooterStyled>
      )}
      <FooterSmall {...footerSmallProps} />
    </>
  );
};

export default FooterBig;
